/**
 * The SINGLE canonical numeric grammar for the whole uploader. Both ingest (the
 * persisted `numericValue` via {@link rawCleanCellValue} → `normalizeNumeric`) and
 * type inference go through {@link parseNumericValue}, so "is this cell a number"
 * and "what number is it" can never disagree across the parse, the projection, and
 * the SQL aggregate. (This retires the old split between `isNumericValue` and
 * `normalizeNumeric` — the drift the `fact-inference` PARITY NOTE documented.)
 *
 * The exact, ordered rules are the source of truth for the public parsing-rules
 * page; the canonical input → output table lives in {@link ./parsing-rules} and is
 * asserted against this parser by `numeric-parse.test.ts`, so the page can never
 * drift from the parser. KEEP THIS GRAMMAR AND ./parsing-rules IN SYNC.
 */

/** Currency symbols stripped from a single leading position. */
export const CURRENCY_SYMBOLS = ['$', '€', '£', '¥'] as const;
/** ISO currency codes stripped (leading OR trailing, one only), case-insensitive. */
export const CURRENCY_CODES = [
  'USD',
  'EUR',
  'GBP',
  'JPY',
  'CAD',
  'AUD',
  'CHF',
  'CNY',
] as const;

const CODE_TRAILING = new RegExp(`\\s*(${CURRENCY_CODES.join('|')})\\s*$`, 'i');
const CODE_LEADING = new RegExp(`^\\s*(${CURRENCY_CODES.join('|')})\\s*`, 'i');
/** Strict decimal grammar: optional sign, then digits / decimal. No exponent, no units. */
const DECIMAL = /^[+-]?(\d+(\.\d+)?|\.\d+)$/;
/**
 * Grouped-decimal grammar (F19/F193): a comma-bearing remainder must be a leading
 * group of 1-3 digits WITHOUT a leading zero, followed by one-or-more EXACT
 * 3-digit comma groups, with an optional single `.` decimal tail. Only then are
 * the commas thousands separators and safe to strip. Anything else (`1,5`,
 * `12,34`, `1,2345`, European `1.234,56`) is NOT a number under this grammar —
 * reject to text, never a different number. The no-leading-zero rule exists
 * because a zero leading group (`0,125`) is never written in US thousands
 * grouping — its only realistic meaning is a European decimal (0.125), so
 * reading it as 125 would book a 1000x-wrong figure. (Leading zeros WITHOUT
 * commas, `0123`, stay accepted by the plain decimal grammar — zero-padding is
 * unambiguous.)
 */
const GROUPED_DECIMAL = /^[+-]?[1-9]\d{0,2}(,\d{3})+(\.\d+)?$/;

// ── Storage-magnitude capacity (P1 — F14/F31/F135) ───────────────────────────
//
// The PARSER is fully lossless — it never rejects or clamps a large number, and
// the ingest ledger's `parsedNumerics` JSONB cache keeps the value verbatim. The
// only fixed-precision sinks in the pipeline are the PROJECTION `Decimal(20,6)`
// columns (`portfolio_loan_observations.numericValue`,
// `portfolio_loan_attributes.numericValue`, `portfolio_loan_latest_values.numericValue`,
// `portfolio_transactions.amount` — schema.prisma). Decimal(20,6) holds
// 20 − 6 = 14 integer digits, so any |value| ≥ 10^14 would throw
// `numeric field overflow` at the projection INSERT. The projection instead
// NULL-coerces such a value ("cannot be represented at Decimal(20,6)") and counts
// it as a file-level data-quality signal; these constants pin that boundary in
// ONE place so the SQL guard and the tests cannot drift from the schema.

/** Integer digits a projection `Decimal(20,6)` column can hold (20 − 6). */
export const NUMERIC_MAX_INTEGER_DIGITS = 14;
/** Fractional digits the projection `Decimal(20,6)` columns store. */
export const NUMERIC_SCALE = 6;

/**
 * True when a parsed value's magnitude exceeds what the projection's
 * `Decimal(20,6)` sinks can store (|value| ≥ 10^{@link NUMERIC_MAX_INTEGER_DIGITS}).
 * A pure predicate over the FINAL stored magnitude — callers must apply any
 * percent ×100 scaling BEFORE asking, so scaling can never push a near-boundary
 * value over undetected.
 */
export function exceedsStorageMagnitude(value: number): boolean {
  return Math.abs(value) >= 10 ** NUMERIC_MAX_INTEGER_DIGITS;
}

/** A numeric parse plus the decorations that drive display-subtype inference. */
export interface NumericParse {
  /** The parsed number, or null when the raw value is not a number under the grammar. */
  readonly value: number | null;
  /**
   * The raw value carried a literal `%`. The `%` is STRIPPED (a decoration), never
   * rescaled: `5%` parses to the bare numeral `5`, not `0.05`. This flag feeds ONLY
   * subtype inference (the percent-marked share of a column); the authoritative
   * percent scaling now happens at projection, driven by the field's chosen subtype.
   */
  readonly hadPercent: boolean;
  /** The raw value carried a currency symbol or ISO code. */
  readonly hadCurrency: boolean;
}

const NOT_NUMERIC: NumericParse = { value: null, hadPercent: false, hadCurrency: false };

/**
 * Parse a raw cell to a number under the one canonical grammar, surfacing the
 * decorations that {@link inferNumericSubtype} reads. Ordered rules:
 *
 *  1. Trim surrounding whitespace.
 *  2. Peel the outer decorations ORDER-INDEPENDENTLY, each at most once, until
 *     none applies:
 *       - a matching outer parenthesis pair `( … )` ⇒ negative MAGNITUDE;
 *       - ONE leading currency symbol (`$ € £ ¥`);
 *       - ONE leading `+`/`-` sign (`-` ⇒ negative MAGNITUDE).
 *     So `-$500`, `$-500`, `($500)`, `$(1,234.00)`, and `$ (1,234.00)` all reduce
 *     to the same core numeral with the negative flag set. Negative is a sticky
 *     MAGNITUDE flag (the result is `-|x|`), so a redundant `(-5)` is -5, never +5.
 *  3. Strip ONE currency ISO code (`USD`…), leading or trailing, only if a
 *     digit-bearing remainder is left (so a bare `USD` stays text, not a number).
 *  4. A trailing `%` is STRIPPED as a decoration (`5%` → 5), never rescaled. The
 *     parser does NO scaling: `numericValue` is always the bare numeral as written,
 *     decorations removed. The `%` is recorded on {@link NumericParse.hadPercent} to
 *     drive subtype inference only; the authoritative ×100 for a `percentage`-subtype
 *     column happens at PROJECTION, where the field's chosen subtype is known.
 *  5. Thousands commas are stripped ONLY when they group digits correctly (a
 *     leading 1-3-digit group with no leading zero, then exact 3-digit groups:
 *     `1,234`, `1,234,567`, `12,345.6789`). A malformed grouping (`1,5`,
 *     `12,34`, `1,2345`, zero-led `0,125`) or a European-style decimal comma
 *     (`1.234,56`) is NOT a number — it is kept as text, never silently
 *     reinterpreted as a different number (F19/F193).
 *  6. The remainder must match the strict decimal grammar (no exponent, no `5.`,
 *     no magnitude suffixes like `125k`/`1.2M`, no hex/Infinity/NaN); else null.
 *     `Number()` the core; apply the negative-magnitude flag (`-|x|`); normalize
 *     `-0 → 0`.
 *
 * The parser is LOSSLESS at any magnitude: a 15–20-digit value parses to its
 * number (the ingest cache keeps it verbatim). The `Decimal(20,6)` storage
 * capacity ({@link exceedsStorageMagnitude}) is a PROJECTION-time rule, not a
 * parse rule.
 *
 * Blank/null-like tokens (`''`, `NA`, `-`, …) are not special-cased here: they all
 * fail the decimal grammar and return null on their own. (A bare `-` peels as a
 * sign into an empty core, which fails the grammar — still null.)
 */
export function parseNumericValueDetailed(raw: string): NumericParse {
  let s = raw.trim();
  if (s === '') return NOT_NUMERIC;

  // Step 2 — order-independent decoration peeling. Each category fires at most
  // once; loop until a full pass changes nothing. Every peel strictly shrinks the
  // string, so this terminates.
  let negative = false;
  let hadCurrency = false;
  let peeledParens = false;
  let peeledSymbol = false;
  let peeledSign = false;
  for (;;) {
    let changed = false;
    if (!peeledParens && s.startsWith('(') && s.endsWith(')') && s.length >= 2) {
      negative = true;
      peeledParens = true;
      s = s.slice(1, -1).trim();
      changed = true;
    }
    if (!peeledSymbol) {
      for (const sym of CURRENCY_SYMBOLS) {
        if (s.startsWith(sym)) {
          s = s.slice(sym.length).trim();
          hadCurrency = true;
          peeledSymbol = true;
          changed = true;
          break;
        }
      }
    }
    if (!peeledSign && (s.startsWith('-') || s.startsWith('+'))) {
      if (s.startsWith('-')) negative = true;
      peeledSign = true;
      s = s.slice(1).trim();
      changed = true;
    }
    if (!changed) break;
  }
  if (s === '') return NOT_NUMERIC;

  // Step 3 — one ISO currency code, leading or trailing, digit-bearing remainder only.
  const trailingCode = CODE_TRAILING.exec(s);
  if (trailingCode) {
    const rest = s.slice(0, trailingCode.index).trim();
    if (rest !== '') {
      s = rest;
      hadCurrency = true;
    }
  } else {
    const leadingCode = CODE_LEADING.exec(s);
    if (leadingCode) {
      const rest = s.slice(leadingCode[0].length).trim();
      if (rest !== '') {
        s = rest;
        hadCurrency = true;
      }
    }
  }

  // Step 4 — trailing % is a stripped decoration (recorded, never rescaled).
  let hadPercent = false;
  if (s.endsWith('%')) {
    hadPercent = true;
    s = s.slice(0, -1).trim();
  }

  // Step 5 — thousands-separator commas, with GROUPING VALIDATION (F19/F193): a
  // comma-bearing remainder must match the grouped-decimal grammar exactly, else
  // the value is not a number (kept as text) — a malformed grouping is NEVER
  // silently reinterpreted (`1,5` is not 15; European `1.234,56` is not 1.23456).
  if (s.includes(',')) {
    if (!GROUPED_DECIMAL.test(s)) return NOT_NUMERIC;
    s = s.replace(/,/g, '');
  }

  // Step 6 — strict decimal core. The core's own optional sign (DECIMAL) covers a
  // sign that no peel reached (e.g. `USD -5`, where the code strip exposes it), but
  // a sign was already peeled ⇒ a second leading sign is a double sign (`--5`),
  // which the strict grammar rejects — decorations peel at most once each.
  if (peeledSign && (s.startsWith('-') || s.startsWith('+'))) return NOT_NUMERIC;
  if (s === '' || !DECIMAL.test(s)) return NOT_NUMERIC;

  let n = Number(s);
  if (!Number.isFinite(n)) return NOT_NUMERIC;
  // The `%` is a stripped decoration, not a rescale: the cache stores the bare numeral.
  // Percent scaling is applied at projection from the field's chosen subtype.
  // Negative is a MAGNITUDE flag: the result is -|x|, so `(-5)` is -5, never +5.
  if (negative) n = -Math.abs(n);
  // Normalize -0 to 0 so the stored/displayed value is canonical.
  if (n === 0) n = 0;
  return { value: n, hadPercent, hadCurrency };
}

/** The canonical numeric parse: the number, or null when the value is not numeric. */
export function parseNumericValue(raw: string): number | null {
  return parseNumericValueDetailed(raw).value;
}
